import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  Divider,
  Grid,
  GridItem,
  ScrollBox,
  SensitiveInfoIcon,
  ToolTip,
  Typography,
  useAlert,
} from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { PrintClientApprovalDetailEntity } from 'src/api/user/printClientApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, FileIcon, ShowLineContent, UserDetail } from '.';

import './PrintClientDetail.scss';

interface LogStatus {
  txtPrint: boolean;
  imgPrint: boolean;
  txtDetect: boolean;
  imgDetect: boolean;
}

interface PrintClientApprovalDetailProps extends CommonProps {
  printClientApprovalDetail: PrintClientApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReset?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

const PrintClientDetail: React.FC<PrintClientApprovalDetailProps> = ({
  printClientApprovalDetail,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const { alert, confirm } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();

  let approvers: ApprovalUserInfoDto[] = [];
  if (printClientApprovalDetail.completeApprovers.length > 0) {
    const completes = _.sortBy(printClientApprovalDetail.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (printClientApprovalDetail.progressApprovers.length > 0) {
    const progresses = _.sortBy(printClientApprovalDetail.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }

  approvers = approvers.concat(
    printClientApprovalDetail.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  const checkUsageInfoLen = (type: string): string => {
    if (type === 'count') {
      return printClientApprovalDetail.usageInfos.length
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    let tempTotalCount = 0;
    printClientApprovalDetail.usageInfos.forEach((info) => {
      tempTotalCount += info.detectedCount;
    });

    return tempTotalCount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handlePrintPrintRange = (): string => {
    const tempPrintRange: string[] = [];
    // console.log('client detail: ', printClientApprovalDetail);
    if (printClientApprovalDetail.exceptWat === 'Y') {
      tempPrintRange.push(t('userApproval:watermarkPrint'));
    }
    if (printClientApprovalDetail.exceptMask === 'Y') {
      tempPrintRange.push(t('userApproval:maskingPrint'));
    }
    return tempPrintRange.map((item) => item).join(' · ');
  };

  // 적용중인 정책 리스트 출력 함수.
  const handlePrintPolicyType = (): React.JSX.Element => {
    return (
      <div>
        {printClientApprovalDetail.printType.map((policyType) => (
          <div className="pb-6">{t(`userApproval:printPolicyType.${policyType}`)}</div>
        ))}
      </div>
    );
  };
  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (printClientApprovalDetail.progressApprovers.length > 0) {
      const progressApproverInfo = printClientApprovalDetail.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: printClientApprovalDetail.approvalNo,
        approvalMethod: printClientApprovalDetail.approvalMethod,
        approvalType: printClientApprovalDetail.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then((resp) => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="printClient-root">
      <div
        className={classNames('printClient-detail-maincontainer', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={printClientApprovalDetail.approvalType}
          status={printClientApprovalDetail.status}
        />

        {/* 대상 문서 */}
        <div className="detail-body">
          {!printClientApprovalDetail.fileDetails || (
            <ShowLineContent title={t('userApproval:fileDetails.$')}>
              <Grid spacing={0}>
                <GridItem xs={12} padding="24px 0px 0px 0px">
                  <Typography variant="h4">{t('userApproval:fileDetails.$')}</Typography>
                </GridItem>

                <GridItem xs={12}>
                  <ToolTip
                    useEllipsis
                    textAlign="center"
                    horiozionPosition="center"
                    message={printClientApprovalDetail.fileDetails[0].fileName}
                  >
                    <div className="pt-10 txt-ellipsis">
                      {FileIcon(printClientApprovalDetail.fileDetails[0].fileName)}
                      {printClientApprovalDetail.fileDetails[0].fileName}
                    </div>
                  </ToolTip>
                </GridItem>
              </Grid>
              <div>
                <div className="mg mt-13 printClient-subTitle">
                  {t(`userApproval:fileDetails.docType.title`)}
                </div>
                <div className="mg mt-6">
                  {t(`userApproval:fileDetails.docType.${printClientApprovalDetail.docType}`)}
                </div>
                <div className="mg mt-16 printClient-subTitle">
                  {t(`userApproval:fileDetails.procName`)}
                </div>
                <div className="mg mt-6">{printClientApprovalDetail.exceptApp}</div>
              </div>
            </ShowLineContent>
          )}

          {/* 적용 중인 정책 */}
          {printClientApprovalDetail.printType.length > 0 && (
            <ShowLineContent
              classes="mg mt-32"
              title={t(`userApproval:printPolicyType.title`)}
              content={handlePrintPolicyType()}
            />
          )}

          {/* 예외 범위 */}
          {printClientApprovalDetail.exceptWat === 'Y' ||
          printClientApprovalDetail.exceptMask === 'Y' ? (
            <ShowLineContent
              classes="mg mt-32"
              title={t(`userApproval:exRange`)}
              content={handlePrintPrintRange()}
            />
          ) : null}

          {/* 민감 정보 검출 */}
          {printClientApprovalDetail.usageInfos.length !== undefined &&
            printClientApprovalDetail.usageInfos.length > 0 && (
              <ShowLineContent
                classes="mg mt-32"
                title={t(`userApproval:secDataDetection`)}
                content={
                  <div>
                    <div>
                      <Trans i18nKey="userApproval:totalPattern">
                        총<span className="printClient-subTitle" />
                        <span className="file-count fs-12">
                          {{ n: checkUsageInfoLen('count') }}
                        </span>
                        <span className="printClient-subTitle" />
                      </Trans>
                    </div>
                    <div className="mg mt-6 mb-8">
                      <Trans i18nKey="userApproval:totalPatternInfoCount">
                        총<span className="printClient-subTitle" />
                        <span className="file-count fs-12">
                          {{ n: checkUsageInfoLen('totalCount') }}
                        </span>
                        <span className="printClient-subTitle" />
                      </Trans>
                    </div>
                    <ScrollBox maxHeight={324}>
                      {printClientApprovalDetail.usageInfos.map((sensitiveInfoItem) => (
                        <div
                          key={sensitiveInfoItem.patternId}
                          className={classNames('printClient-sensitiveInfoRow')}
                        >
                          <SensitiveInfoIcon />

                          <div className="printClient-sensitiveInfoRow-Item">
                            <div>{sensitiveInfoItem.patternName}</div>
                            <div className="printClient-sensitiveInfoRow-count">
                              {sensitiveInfoItem.detectedCount}
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollBox>
                  </div>
                }
              />
            )}

          {/* 인쇄 고유 번호 */}
          <ShowLineContent
            classes="mg mt-28 mb-32"
            title={t('userApproval:printuuid')}
            content={printClientApprovalDetail.transId}
          />

          {/* 승인 방법 */}
          {!printClientApprovalDetail.approvalMethod || (
            <ShowLineContent
              classes={classNames('approval-method', 'mg mt-28 mb-32')}
              title={t('userApproval:approvalMethod.$')}
              content={t(`userApproval:approvalMethod.${printClientApprovalDetail.approvalMethod}`)}
            />
          )}
          <ShowLineContent
            title={t('userApproval:applicant')}
            content={
              <UserDetail
                type="request"
                dateStr={toLocalDateTimeLangStr(
                  printClientApprovalDetail.applyDate,
                  getFasooUiComponentLanguage()
                )}
                remark={printClientApprovalDetail.remark}
                userInfo={{
                  userId: printClientApprovalDetail.userId,
                  userName: printClientApprovalDetail.userName,
                  positionName: printClientApprovalDetail.positionName,
                  deptName: printClientApprovalDetail.deptName,
                  actorIp: printClientApprovalDetail.actorIp,
                }}
              />
            }
          />
        </div>
        <div className="approval-detail-info-title">
          <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
        </div>
        <div className="detail-body">
          <ShowLineContent
            title=""
            content={
              <>
                {approvers.map((approver, idx) => {
                  return (
                    <UserDetail
                      step={approvers.length > 1 ? approver.step : undefined}
                      type="approver"
                      dateStr={
                        approver.approvalDate
                          ? toLocalDateTimeLangStr(
                              approver.approvalDate,
                              getFasooUiComponentLanguage()
                            )
                          : null
                      }
                      remark={approver.approvalRemark ? approver.approvalRemark : null}
                      userInfo={approver}
                      key={`approver-${idx}`}
                    />
                  );
                })}

                {approvers.length <= 1 || <Divider />}
              </>
            }
          />

          {printClientApprovalDetail.status !== ApprovalStatusType.APPLY && (
            <ShowLineContent
              title={t('userApproval:status.$')}
              content={
                <div className="approval-status">
                  <span className={`approval-status-${printClientApprovalDetail.status}`} />
                  {t(`userApproval:status.${printClientApprovalDetail.status}`)}
                </div>
              }
            />
          )}
          {cancelApprovalChildren}
        </div>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={printClientApprovalDetail.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={printClientApprovalDetail}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default PrintClientDetail;
